/**
 * ProvidersTab Component (T117 - US5)
 * Complete provider management interface with edit and delete operations
 * 
 * Integrates:
 * - ProviderTable for displaying active providers
 * - PendingProvidersCard for displaying pending providers
 * - FilterPanel for search and status filters
 * - PaginationControls for navigation
 * - ProviderForm for editing
 * - React Query hooks for data management
 */

import { useState } from 'react';
import { AlertCircle, Users, Plus } from 'lucide-react';
import { ProviderTable } from './ProviderTable';
import { ProviderTableSkeleton } from './ProviderTableSkeleton';
import { PendingProvidersCard } from './PendingProvidersCard';
import { FilterPanel } from './FilterPanel';
import { PaginationControls } from './PaginationControls';
import { ProviderForm } from './ProviderForm';
import { ProviderEditorPage } from './provider-editor/ProviderEditorPage';
import { usePagination } from '../hooks/usePagination';
import {
  useAdminProviders,
  useUpdateProvider,
  useDeleteProvider,
} from '../hooks/useAdminProviders';
import type {
  AdminProvider,
  UpdateProviderPayload,
} from '../types/admin-provider.type';

type ViewMode = 'list' | 'create' | 'edit';
type FormMode = 'edit' | null;

/**
 * Providers tab component with full management functionality
 * 
 * Features:
 * - List active providers with pagination
 * - List pending providers in card format
 * - Search by company name
 * - Filter by vetting status (pending, in_review, approved, rejected)
 * - Edit existing providers
 * - Soft-delete providers
 * - Optimistic updates
 * - Loading states
 * - Error handling
 * - Total count reflects applied filters
 */
export function ProvidersTab() {
  // View mode state - determines what to render
  const [viewMode, setViewMode] = useState<ViewMode>('list');
  const [editProviderId, setEditProviderId] = useState<string | undefined>();

  // State management
  const [formMode, setFormMode] = useState<FormMode>(null);
  const [selectedProvider, setSelectedProvider] = useState<AdminProvider | undefined>();
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<'' | 'pending' | 'in_review' | 'approved' | 'rejected'>('');
  const [deleteConfirm, setDeleteConfirm] = useState<AdminProvider | null>(null);
  const [viewSection, setViewSection] = useState<'active' | 'pending'>('active');

  // Pagination for active providers
  const pagination = usePagination({
    initialPage: 1,
    initialPageSize: 50,
  });

  // Data fetching for active providers
  const { data: activeData, isLoading: isLoadingActive, error: activeError, refetch: refetchActive } = useAdminProviders({
    page: pagination.page,
    pageSize: pagination.pageSize,
    search: search || undefined,
    status: statusFilter || undefined,
  });

  // Data fetching for pending providers
  const { data: pendingData, isLoading: isLoadingPending } = useAdminProviders({
    page: 1,
    pageSize: 10, // Show top 10 pending
    status: 'pending',
  });

  // Mutations
  const updateProvider = useUpdateProvider();
  const deleteProvider = useDeleteProvider();

  // Handlers
  const handleEditProvider = (providerId: string) => {
    // Switch to edit mode with the selected provider
    setEditProviderId(providerId);
    setViewMode('edit');
  };

  const handleDeleteProvider = (providerId: string) => {
    const provider = activeData?.items.find((p) => p.id === providerId);
    if (provider) {
      setDeleteConfirm(provider);
    }
  };

  const handleFormSubmit = async (payload: UpdateProviderPayload) => {
    try {
      if (formMode === 'edit' && selectedProvider) {
        await updateProvider.mutateAsync({
          id: selectedProvider.id,
          payload,
        });
      }
      
      setFormMode(null);
      setSelectedProvider(undefined);
    } catch (error) {
      console.error('Form submission error:', error);
      throw error; // Re-throw to keep form open
    }
  };

  const handleFormCancel = () => {
    setFormMode(null);
    setSelectedProvider(undefined);
  };

  const handleConfirmDelete = async () => {
    if (!deleteConfirm) return;

    try {
      await deleteProvider.mutateAsync(deleteConfirm.id);
      setDeleteConfirm(null);
    } catch (error) {
      console.error('Delete error:', error);
    }
  };

  const handleCancelDelete = () => {
    setDeleteConfirm(null);
  };

  const handleSearchChange = (value: string) => {
    setSearch(value);
    pagination.goToFirstPage();
  };

  const handleStatusChange = (value: string) => {
    setStatusFilter(value as typeof statusFilter);
    pagination.goToFirstPage();
  };

  const handleCreateProvider = () => {
    setEditProviderId(undefined);
    setViewMode('create');
  };

  const handleBackToList = () => {
    setViewMode('list');
    setEditProviderId(undefined);
    refetchActive();
  };

  // Render ProviderEditorPage when in create or edit mode
  if (viewMode === 'create' || viewMode === 'edit') {
    return (
      <ProviderEditorPage
        mode={viewMode}
        providerId={editProviderId}
        onBack={handleBackToList}
      />
    );
  }

  // Error state
  if (activeError) {
    return (
      <div className="flex flex-col items-center justify-center py-12">
        <AlertCircle className="h-12 w-12 text-red-500 mb-4" />
        <h3 className="text-lg font-semibold text-gray-900 mb-2">
          Failed to load providers
        </h3>
        <p className="text-gray-600 mb-4">{activeError.message}</p>
        <button
          type="button"
          onClick={() => refetchActive()}
          className="px-4 py-2 bg-[#026857] text-white rounded-md hover:bg-[#025246] transition-colors"
        >
          Try Again
        </button>
      </div>
    );
  }

  return (
    <div data-testid="providers-tab" className="flex flex-col h-full">
      {/* Header */}
      <div className="px-6 py-4 border-b border-gray-200 bg-white">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Provider Management</h2>
            <p className="text-sm text-gray-600 mt-0.5">
              Manage service provider profiles and vetting status
            </p>
          </div>
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={handleCreateProvider}
              className="inline-flex items-center rounded-md border border-transparent bg-[#026857] px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-[#025246] focus:outline-none focus:ring-2 focus:ring-[#026857] focus:ring-offset-2"
            >
              <Plus className="mr-2 h-4 w-4" />
              Create Provider
            </button>
          </div>
        </div>

        {/* Section tabs */}
        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => setViewSection('active')}
            className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
              viewSection === 'active'
                ? 'bg-[#026857] text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            <div className="flex items-center gap-2">
              <Users className="h-4 w-4" />
              <span>Active Providers</span>
              {activeData && (
                <span className="ml-1 px-2 py-0.5 bg-white/20 rounded-full text-xs">
                  {activeData.meta.total}
                </span>
              )}
            </div>
          </button>
          <button
            type="button"
            onClick={() => setViewSection('pending')}
            className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
              viewSection === 'pending'
                ? 'bg-[#026857] text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            <div className="flex items-center gap-2">
              <span>Pending Review</span>
              {pendingData && pendingData.meta.total > 0 && (
                <span className="ml-1 px-2 py-0.5 bg-yellow-500 text-white rounded-full text-xs">
                  {pendingData.meta.total}
                </span>
              )}
            </div>
          </button>
        </div>
      </div>

      {/* Active Providers Section */}
      {viewSection === 'active' && (
        <>
          {/* Filter panel */}
          <FilterPanel
            searchValue={search}
            onSearchChange={handleSearchChange}
            statusValue={statusFilter}
            onStatusChange={handleStatusChange}
            searchPlaceholder="Search by company name..."
            showRoleFilter={false}
            statusOptions={[
              { value: '', label: 'All Statuses' },
              { value: 'pending', label: 'Pending' },
              { value: 'in_review', label: 'In Review' },
              { value: 'approved', label: 'Approved' },
              { value: 'rejected', label: 'Rejected' },
            ]}
          />

          {/* Table content */}
          <div className="flex-1 overflow-auto px-6 py-4">
            {isLoadingActive ? (
              <ProviderTableSkeleton rows={pagination.pageSize} />
            ) : activeData ? (
              <ProviderTable
                providers={activeData.items}
                totalCount={activeData.meta.total}
                isFiltered={!!(search || statusFilter)}
                onEdit={handleEditProvider}
                onDelete={handleDeleteProvider}
              />
            ) : null}
          </div>

          {/* Pagination controls */}
          {activeData && (
            <PaginationControls
              meta={activeData.meta}
              onPageChange={pagination.setPage}
              onPageSizeChange={pagination.setPageSize}
            />
          )}
        </>
      )}

      {/* Pending Providers Section */}
      {viewSection === 'pending' && (
        <div className="flex-1 overflow-auto px-6 py-4">
          <div className="mb-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-1">
              Pending Provider Applications
            </h3>
            <p className="text-sm text-gray-600">
              Review and approve provider applications.
            </p>
          </div>
          
          {isLoadingPending ? (
            <div className="space-y-3">
              {Array.from({ length: 3 }).map((_, index) => (
                <div
                  key={index}
                  className="bg-white border border-gray-200 rounded-lg p-4 space-y-3"
                >
                  <div className="h-5 w-48 bg-gray-200 rounded animate-pulse" />
                  <div className="h-4 w-64 bg-gray-100 rounded animate-pulse" />
                  <div className="h-4 w-32 bg-gray-100 rounded animate-pulse" />
                </div>
              ))}
            </div>
          ) : (
            <PendingProvidersCard
              providers={pendingData?.items || []}
              onView={handleEditProvider}
            />
          )}
        </div>
      )}

      {/* Provider form modal */}
      {formMode === 'edit' && selectedProvider && (
        <ProviderForm
          provider={selectedProvider}
          onSubmit={handleFormSubmit}
          onCancel={handleFormCancel}
          isSubmitting={updateProvider.isPending}
        />
      )}

      {/* Delete confirmation modal */}
      {deleteConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              Delete Provider
            </h3>
            <p className="text-gray-600 mb-6">
              Are you sure you want to delete{' '}
              <span className="font-medium">
                {deleteConfirm.companyName || 
                 `${deleteConfirm.user.firstName} ${deleteConfirm.user.lastName || ''}`}
              </span>
              ? This action will soft-delete the provider and they will no longer
              appear in lists.
            </p>
            <div className="flex items-center justify-end gap-3">
              <button
                type="button"
                onClick={handleCancelDelete}
                disabled={deleteProvider.isPending}
                className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleConfirmDelete}
                disabled={deleteProvider.isPending}
                className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors disabled:opacity-50 flex items-center gap-2"
              >
                {deleteProvider.isPending ? (
                  <>
                    <div className="h-4 w-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    <span>Deleting...</span>
                  </>
                ) : (
                  <span>Delete Provider</span>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
